import { useState, useEffect } from "react";
import { X, SlidersHorizontal } from "lucide-react";
import { CORAL, F, T } from "../../theme/tokens";

// ── Filter Drawer ─────────────────────────────────────────────────────────────

export type FilterFieldDef =
  | { type: "chips"; key: string; label: string; options: string[] }
  | { type: "select"; key: string; label: string; options: string[] }
  | { type: "date"; key: string; label: string };

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  fields: FilterFieldDef[];
  values: Record<string, string>;
  onApply: (v: Record<string, string>) => void;
}

export function FilterDrawer({ open, onClose, title, fields, values, onApply }: FilterDrawerProps) {
  const [draft, setDraft] = useState<Record<string, string>>(values);

  useEffect(() => { if (open) setDraft(values); }, [open]);

  function reset() {
    const empty: Record<string, string> = {};
    fields.forEach((f) => { empty[f.key] = f.type === "chips" ? "All" : ""; });
    setDraft(empty);
  }

  const activeCount = Object.entries(draft).filter(([k, v]) => {
    const field = fields.find((f) => f.key === k);
    if (!field) return false;
    return field.type === "chips" ? v !== "All" : v !== "";
  }).length;

  return (
    <>
      {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 600 }} />}
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100dvh", width: "min(340px, 100vw)",
        background: T.surface, borderLeft: `1px solid ${T.border}`,
        zIndex: 700, transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
        display: "flex", flexDirection: "column",
        boxShadow: open ? "-12px 0 40px rgba(0,0,0,0.1)" : "none",
        fontFamily: F,
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <SlidersHorizontal size={15} style={{ color: T.sub }} />
            <span style={{ fontSize: "14px", fontWeight: 700, color: T.text }}>{title} Filters</span>
            {activeCount > 0 && (
              <span style={{ background: CORAL, color: "#fff", borderRadius: "9999px", fontSize: "10px", fontWeight: 700, padding: "1px 7px", lineHeight: "16px" }}>{activeCount}</span>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, lineHeight: 0, padding: "4px" }}>
            <X size={16} />
          </button>
        </div>

        {/* Fields */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: "24px", scrollbarWidth: "none" }}>
          {fields.map((field) => (
            <div key={field.key}>
              <p className="fd-label" style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase", marginBottom: "10px" }}>{field.label}</p>
              {field.type === "chips" && (
                <div className="fd-chips" style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {field.options.map((opt) => {
                    const active = (draft[field.key] ?? "All") === opt;
                    return (
                      <button key={opt} className="fd-chip" data-active={active ? "1" : undefined} onClick={() => setDraft((p) => ({ ...p, [field.key]: opt }))}
                        style={{ padding: "5px 13px", borderRadius: "9999px", fontSize: "11px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${active ? T.text : T.border}`, background: active ? T.text : T.surface, color: active ? "#fff" : T.sub, transition: "all 0.12s" }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
              {field.type === "select" && (
                <select value={draft[field.key] ?? ""} onChange={(e) => setDraft((p) => ({ ...p, [field.key]: e.target.value }))}
                  style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "8px 12px", fontSize: "12px", fontFamily: F, outline: "none", color: T.text, background: T.surface, cursor: "pointer" }}>
                  {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              {field.type === "date" && (
                <input type="date" value={draft[field.key] ?? ""} onChange={(e) => setDraft((p) => ({ ...p, [field.key]: e.target.value }))}
                  style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: "8px", padding: "8px 12px", fontSize: "12px", fontFamily: F, outline: "none", color: T.text, background: T.surface, cursor: "pointer", boxSizing: "border-box" }} />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 20px", borderTop: `1px solid ${T.border}`, display: "flex", gap: "10px", flexShrink: 0, background: T.surface }}>
          <button onClick={reset} style={{ flex: 1, padding: "9px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 600, cursor: "pointer", border: `1px solid ${T.border}`, background: T.s2, color: T.sub, transition: "all 0.15s" }}>
            Reset
          </button>
          <button onClick={() => { onApply(draft); onClose(); }} style={{ flex: 2, padding: "9px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 700, cursor: "pointer", border: "none", background: CORAL, color: "#fff", transition: "all 0.15s" }}>
            Apply filters{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
        </div>
      </div>
    </>
  );
}
